import type { Actor } from './actor';
import type { HitResult } from './combat';
import type { SkillDef, TripodDef } from './skillTypes';
import type { BaseStats } from './stats';
import type { Element, Grade } from './types';
import type { World } from './world';

/** Class identity ("Resonance") behaviour plugged into the generic sim. */
export interface IdentityController {
  gaugeMax: number;
  /** Called when a player of this class is created or respawned. */
  init(actor: Actor): void;
  /** Damage dealt by this actor (after resolution). */
  onDamageDealt?(world: World, actor: Actor, skill: SkillDef | null, amount: number, counter: boolean): void;
  onSkillCast?(world: World, actor: Actor, skill: SkillDef): void;
  /** Z/X presses. Return true when handled. */
  onKey?(world: World, actor: Actor, key: 'Z' | 'X'): boolean;
  update?(world: World, actor: Actor, dt: number): void;
  /** Replaces the skill bound to a slot (forms / stances). */
  skillForSlot?(actor: Actor, slotIndex: number, base: string | null): string | null;
  /** Replaces the movement skill bound to Space (e.g. a transformation's own dash). */
  dashFor?(actor: Actor, base: string): string;
  /** Veto casting (e.g. wrong stance / not enough orbs). */
  canCast?(actor: Actor, skill: SkillDef): boolean;
  /** Extra damage multiplier for a skill in the current identity state (`target` = the victim, e.g. "+30% vs staggered"). */
  damageMult?(actor: Actor, skill: SkillDef, target?: Actor): number;
  /**
   * An enemy hit is about to land on this player (victim side, after invulnerability
   * checks). Return true to negate it entirely (parry / block stances).
   */
  onIncomingHit?(world: World, actor: Actor, hit: HitResult): boolean;
  /** Identity "op" actions fired from skill timelines. */
  op?(world: World, actor: Actor, op: string, value: number): void;
  /** Human readable state for the HUD. */
  describe?(actor: Actor): { label: string; ready: boolean; active: boolean; pips?: number; pipsMax?: number };
}

export interface ClassDef {
  id: string;
  name: string;
  title: string;
  archetype: string;
  role: 'dps' | 'support';
  description: string;
  hue: number;
  weapon: string;
  model: string;
  /** Base stats at a given level before gear. */
  baseStats: (level: number) => Partial<BaseStats>;
  /** All learnable skills (players equip 8). */
  skills: SkillDef[];
  defaultBar: string[];
  basic: SkillDef;
  dash: SkillDef;
  awakenings: SkillDef[];
  /** Skills that only exist through the identity (Z/X, transformation bars). */
  identitySkills: SkillDef[];
  tripods: Record<string, TripodDef[]>;
  identity: IdentityController;
  identityName: string;
  identityDescription: string;
}

export interface PartDef {
  id: string;
  name: string;
  /** Durability per party member. */
  durability: number;
}

export interface MonsterDef {
  id: string;
  name: string;
  grade: Grade;
  model: string;
  scale: number;
  radius: number;
  tint?: number;
  stats: (level: number) => Partial<BaseStats>;
  skills: SkillDef[];
  /** AI brain id registered in src/sim/ai. */
  brain: string;
  aggroRange: number;
  leash: number;
  xp: number;
  loot: string | null;
  weakness?: Element;
  /** Stagger bar size per party member (named/boss). */
  stagger?: number;
  parts?: PartDef[];
  hpBars?: number;
  /** Walk/run speed override. */
  moveSpeed?: number;
  /** Free-form brain parameters. */
  params?: Record<string, number | string | boolean>;
  creature?: string;
  title?: string;
}

class Registry {
  readonly skills = new Map<string, SkillDef>();
  readonly tripods = new Map<string, TripodDef[]>();
  readonly classes = new Map<string, ClassDef>();
  readonly monsters = new Map<string, MonsterDef>();

  registerSkill(def: SkillDef): void {
    this.skills.set(def.id, def);
  }

  registerClass(def: ClassDef): void {
    this.classes.set(def.id, def);
    for (const s of [...def.skills, def.basic, def.dash, ...def.awakenings, ...def.identitySkills]) {
      this.registerSkill(s);
    }
    for (const [skillId, list] of Object.entries(def.tripods)) this.tripods.set(skillId, list);
  }

  registerMonster(def: MonsterDef): void {
    this.monsters.set(def.id, def);
    for (const s of def.skills) this.registerSkill(s);
  }

  skill(id: string): SkillDef {
    const s = this.skills.get(id);
    if (!s) throw new Error(`Unknown skill '${id}'`);
    return s;
  }

  cls(id: string): ClassDef {
    const c = this.classes.get(id);
    if (!c) throw new Error(`Unknown class '${id}'`);
    return c;
  }

  monster(id: string): MonsterDef {
    const m = this.monsters.get(id);
    if (!m) throw new Error(`Unknown monster '${id}'`);
    return m;
  }
}

/** Global content registry, filled once at startup by src/content. */
export const registry = new Registry();
